import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ChevronDown,
  ChevronRight,
  GripVertical,
  Link2,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { REVEAL_ON_HOVER } from '@/components/ui/reveal-on-hover'
import { PROBLEM_TAG_VOCABULARY } from '@/lib/constants/problem-tags'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { useCommandPalette } from '../command-palette'
import type { Series } from '../hooks/use-series'
import type { ProblemEditorData } from '../problem-editor'
import type { CoachingQuestion } from '../types'

interface SeriesSectionProps {
  formData: ProblemEditorData
}

// Sortable series member component for @dnd-kit
interface SortableSeriesMemberProps {
  member: Series['members'][number]
  isCurrentProblem: boolean
  seriesId: string
  onRemove: (seriesId: string, problemId: string) => void
}

function SortableSeriesMember({
  member,
  isCurrentProblem,
  seriesId,
  onRemove,
}: SortableSeriesMemberProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: member.problemId })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-3 px-4 py-2 ${
        isCurrentProblem ? 'bg-primary/5' : 'hover:bg-muted/30'
      } ${isDragging ? 'shadow-lg' : ''}`}
    >
      <button
        type="button"
        className="cursor-grab touch-none active:cursor-grabbing"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <GripVertical className="text-muted-foreground h-4 w-4 flex-shrink-0" />
      </button>
      <span className="bg-primary text-primary-foreground flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium">
        {member.order}
      </span>
      <span className={`flex-1 ${isCurrentProblem ? 'font-medium' : ''}`}>
        {member.problemExternalId ? `#${member.problemExternalId} - ` : ''}
        {member.problemTitle}
        {isCurrentProblem && (
          <span className="text-primary ml-2 text-xs">(current)</span>
        )}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onRemove(seriesId, member.problemId)
        }}
        className={cn(
          REVEAL_ON_HOVER,
          'text-muted-foreground hover:text-destructive flex-shrink-0',
        )}
        title="Remove from series"
        aria-label={`Remove ${member.problemTitle} from series`}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  )
}

export function SeriesSection({ formData }: SeriesSectionProps) {
  const { openCreateSeries, openAddToSeries } = useCommandPalette()
  const utils = trpc.useUtils()
  const [expandedSeries, setExpandedSeries] = useState<Set<string>>(
    () => new Set(),
  )
  // Auto-expand all series once per problem, on first data arrival. This
  // component stays mounted across problem switches, so we key the guard on
  // `formData.id`: a bare boolean would fire once ever (skipping later
  // problems) and the previous problem's expansion would leak into the next.
  // Keying on the id re-arms per problem; not depending on `expandedSeries`
  // keeps a manual collapse from re-triggering the expand.
  const autoExpandedForProblemIdRef = useRef<string | undefined>(undefined)
  const [editingSeriesId, setEditingSeriesId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')

  // Fetch all series, filter client-side by current problem
  const seriesQuery = trpc.admin.series.list.useQuery(undefined, {
    enabled: !!formData.id,
  })

  const problemSeries: Series[] = useMemo(() => {
    const allSeries = seriesQuery.data?.data ?? []
    return allSeries
      .filter((series) =>
        series.members.some((m) => m.problemId === formData.id),
      )
      .map((series) => ({
        ...series,
        members: series.members.map((m) => ({
          ...m,
          problemTitle: m.problemTitle ?? '',
        })),
      }))
  }, [seriesQuery.data, formData.id])

  // Auto-expand all series on initial load
  const loading = seriesQuery.isLoading && !!formData.id
  useEffect(() => {
    if (!formData.id || autoExpandedForProblemIdRef.current === formData.id)
      return
    // Wait for this problem's series to load before marking it handled.
    if (problemSeries.length === 0) return
    autoExpandedForProblemIdRef.current = formData.id
    // Reset expansion for the new problem, then auto-expand all its series.
    setExpandedSeries(new Set(problemSeries.map((s) => s.id)))
  }, [formData.id, problemSeries])

  const updateSeriesMutation = trpc.admin.series.update.useMutation()
  const removeProblemsMutation = trpc.admin.series.removeProblems.useMutation()

  // Convert formData to CoachingQuestion format for the command palette
  const currentProblemAsQuestion: CoachingQuestion = useMemo(
    () => ({
      id: formData.id ?? '',
      name: formData.name,
      externalId: formData.externalId,
      slug: formData.slug,
      difficulty: formData.difficulty as CoachingQuestion['difficulty'],
      premiumRequired: formData.premiumRequired ?? false,
      type: formData.type ?? 'algorithms',
      problemTags: (formData.problemTagSlugs ?? []).map((slug) => {
        const definition = PROBLEM_TAG_VOCABULARY.find((t) => t.slug === slug)
        return { slug, name: definition?.name ?? slug }
      }),
      source: formData.source ?? 'UNKNOWN',
      sourceIcon: formData.sourceIcon,
      url: formData.url,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }),
    [
      formData.id,
      formData.name,
      formData.externalId,
      formData.slug,
      formData.difficulty,
      formData.premiumRequired,
      formData.type,
      formData.problemTagSlugs,
      formData.source,
      formData.sourceIcon,
      formData.url,
    ],
  )

  // Handler for creating a new series with this problem
  const handleCreateSeries = useCallback(() => {
    openCreateSeries(currentProblemAsQuestion)
  }, [openCreateSeries, currentProblemAsQuestion])

  // Handler for adding problems to an existing series
  const handleAddToSeries = useCallback(
    (series: Series) => {
      openAddToSeries({
        id: series.id,
        name: series.name,
        members: series.members,
      })
    },
    [openAddToSeries],
  )

  // Start editing a series name
  const handleStartEditing = useCallback((series: Series) => {
    setEditingSeriesId(series.id)
    setEditingName(series.name)
  }, [])

  // Save the edited series name
  const handleSaveSeriesName = useCallback(
    async (seriesId: string) => {
      const trimmedName = editingName.trim()
      if (!trimmedName) {
        setEditingSeriesId(null)
        setEditingName('')
        return
      }

      const originalSeries = problemSeries.find((s) => s.id === seriesId)
      if (!originalSeries || originalSeries.name === trimmedName) {
        setEditingSeriesId(null)
        setEditingName('')
        return
      }

      try {
        await updateSeriesMutation.mutateAsync({
          id: seriesId,
          name: trimmedName,
        })

        await utils.admin.series.list.invalidate()
        toast.success('Series name updated')
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to update')
      } finally {
        setEditingSeriesId(null)
        setEditingName('')
      }
    },
    [editingName, problemSeries, updateSeriesMutation, utils],
  )

  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent, seriesId: string) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSaveSeriesName(seriesId)
      } else if (e.key === 'Escape') {
        setEditingSeriesId(null)
        setEditingName('')
      }
    },
    [handleSaveSeriesName],
  )

  const toggleSeriesExpanded = (seriesId: string) => {
    setExpandedSeries((prev) => {
      const next = new Set(prev)
      if (next.has(seriesId)) {
        next.delete(seriesId)
      } else {
        next.add(seriesId)
      }
      return next
    })
  }

  // Configure dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Handle drag end for series members
  const handleSeriesDragEnd = useCallback(
    (seriesId: string) => (event: DragEndEvent) => {
      const { active, over } = event

      if (!over || active.id === over.id) return

      const series = problemSeries.find((s) => s.id === seriesId)
      if (!series) return

      const oldIndex = series.members.findIndex(
        (m) => m.problemId === active.id,
      )
      const newIndex = series.members.findIndex((m) => m.problemId === over.id)

      if (oldIndex === -1 || newIndex === -1) return

      const reorderedMembers = arrayMove(
        series.members,
        oldIndex,
        newIndex,
      ).map((m, i) => ({
        ...m,
        order: i + 1,
      }))

      // Optimistically update the query cache for immediate visual feedback
      utils.admin.series.list.setData(undefined, (prev) => {
        if (!prev) return prev
        return {
          ...prev,
          data: prev.data.map((s) =>
            s.id === seriesId ? { ...s, members: reorderedMembers } : s,
          ),
        }
      })

      toast('Order changed (not yet saved)', { icon: '⚠️' })
    },
    [problemSeries, utils],
  )

  const handleRemoveProblemFromSeries = useCallback(
    async (seriesId: string, problemId: string) => {
      const series = problemSeries.find((s) => s.id === seriesId)
      if (!series) return

      const willDeleteSeries = series.members.length <= 2

      if (willDeleteSeries) {
        // eslint-disable-next-line no-alert -- Intentional use for confirmation dialog
        const confirmed = window.confirm(
          `This series only has ${series.members.length} problems. Removing this problem will delete the entire series "${series.name}". Continue?`,
        )
        if (!confirmed) return
      }

      try {
        const result = await removeProblemsMutation.mutateAsync({
          id: seriesId,
          problemIds: [problemId],
        })

        if (result.seriesDeleted) {
          toast.success(`Series "${series.name}" was deleted`)
        } else {
          toast.success('Problem removed from series')
        }

        await utils.admin.series.list.invalidate()
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : 'Failed to remove problem',
        )
      }
    },
    [problemSeries, removeProblemsMutation, utils],
  )

  if (!formData.id) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground">
          Save the problem first to manage series.
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    )
  }

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold">
            <Link2 className="h-5 w-5" />
            Problem Series
          </h3>
          <p className="text-muted-foreground mt-1 text-sm">
            Thematically related problems (e.g., "Best Time to Buy and Sell
            Stock I, II, III, IV")
          </p>
        </div>
        {problemSeries.length === 0 && (
          <Button variant="default" size="sm" onClick={handleCreateSeries}>
            <Plus className="mr-1 h-4 w-4" />
            Create Series
          </Button>
        )}
      </div>

      {problemSeries.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <p className="text-muted-foreground text-sm">
            This problem is not part of any series.
          </p>
          <p className="text-muted-foreground mt-1 text-xs">
            Click "Create Series" above to start a new series with this problem.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {problemSeries.map((series) => (
            <div key={series.id} className="overflow-hidden rounded-lg border">
              {/* Series Header */}
              <div className="bg-muted/50 flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => toggleSeriesExpanded(series.id)}
                    className="transition-opacity hover:opacity-80"
                    aria-label={
                      expandedSeries.has(series.id) ? 'Collapse' : 'Expand'
                    }
                  >
                    {expandedSeries.has(series.id) ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  {editingSeriesId === series.id ? (
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={() => handleSaveSeriesName(series.id)}
                      onKeyDown={(e) => handleEditKeyDown(e, series.id)}
                      className="bg-background focus:ring-primary rounded border px-2 py-0.5 text-sm font-medium focus:ring-2 focus:outline-none"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleStartEditing(series)}
                      className="hover:text-primary cursor-text font-medium transition-colors"
                      title="Click to edit series name"
                    >
                      {series.name}
                    </button>
                  )}
                  <span className="text-muted-foreground text-sm">
                    ({series.members.length} problems)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddToSeries(series)}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Add Problems
                  </Button>
                </div>
              </div>

              {/* Series Members */}
              {expandedSeries.has(series.id) && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleSeriesDragEnd(series.id)}
                >
                  <SortableContext
                    items={series.members.map((m) => m.problemId)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="divide-y">
                      {series.members.map((member) => (
                        <SortableSeriesMember
                          key={member.problemId}
                          member={member}
                          isCurrentProblem={member.problemId === formData.id}
                          seriesId={series.id}
                          onRemove={handleRemoveProblemFromSeries}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
